/**
 * @fileoverview Centralized error messages for the transcript modal form validation.
 *
 * This file contains all error messages used in the Send Transcript modal form validation.
 * It provides a centralized location for managing validation error messages, making it
 * easier to maintain consistency and support localization in the future.
 *
 * @author Palash
 * @version 1.0.0
 */

//#region Email Validation Error Messages

/**
 * Error message displayed when the user email field is empty or not provided.
 * This is a required field validation message.
 */
export const EMAIL_REQUIRED = "Please enter an email address.";

/**
 * Error message displayed when the user email field contains an invalid email format.
 * Validates against standard email format patterns (e.g., user@domain.com).
 */
export const EMAIL_INVALID = "Please enter a valid email address.";

//#endregion

//#region Share With Field Validation Error Messages

/**
 * Error message displayed when neither user email nor share with field is provided.
 * At least one recipient must be specified for the transcript to be sent.
 */
export const SHARE_WITH_REQUIRED = "Please specify at least one recipient.";

/**
 * Error message displayed when the share with field contains invalid email formats.
 * Accepts either "support" as a special keyword or valid comma-separated email addresses.
 */
export const SHARE_WITH_INVALID_FORMAT = 'Please enter either "support" or valid emails.';

/**
 * Error message displayed when the share with field contains invalid email addresses.
 * Validates each email address format and ensures they conform to standard email patterns.
 */
export const SHARE_WITH_EMAIL_INVALID = "One or more email addresses are invalid.";

/**
 * Error message displayed when the share with field contains too many email addresses.
 * Limits the number of additional recipients to prevent spam and ensure deliverability.
 */
export const SHARE_WITH_TOO_MANY = "You can share with up to 5 email addresses only.";

/**
 * Error message displayed when the share with field contains duplicate email addresses.
 * Prevents sending to the same recipient multiple times.
 */
export const SHARE_WITH_DUPLICATE_EMAILS = "Duplicate email addresses are not allowed.";

//#endregion

//#region Note Field Validation Error Messages

/**
 * Error message displayed when the optional note field exceeds the character limit.
 * Prevents excessively long notes that could cause delivery issues or database constraints.
 */
export const NOTE_TOO_LONG = "Note must be 5000 characters or less.";

//#endregion

//#region General Form Validation Error Messages

/**
 * Error message displayed when both email and share with fields are empty.
 * Ensures at least one recipient is specified before allowing form submission.
 */
export const GENERAL_MISSING_BOTH = "One field is required: either your email or share with.";

/**
 * General exception message for unexpected errors that occur during form processing.
 * This message is displayed to the user when an error occurs that is not specifically handled.
 */
export const GENERAL_EXCEPTION = "An unexpected error occurred. Please try again later.";

/**
 * General invalid parameters message for cases where the
 * request parameters do not meet validation criteria.
 */
export const GENERAL_INVALID_PARAMS = "Invalid parameters provided for the request.";

//#endregion

//#region Error Messages Object

/**
 * Centralized error messages object containing all validation error messages
 * for the transcript modal form. This object provides type-safe access to
 * error messages and can be used throughout the application.
 *
 * @readonly
 */
export const ERRORS = {
	EMAIL_REQUIRED,
	EMAIL_INVALID,
	SHARE_WITH_REQUIRED,
	SHARE_WITH_EMAIL_INVALID,
	SHARE_WITH_INVALID_FORMAT,
	SHARE_WITH_TOO_MANY,
	SHARE_WITH_DUPLICATE_EMAILS,
	NOTE_TOO_LONG,
	GENERAL_EXCEPTION,
	GENERAL_MISSING_BOTH,
	GENERAL_INVALID_PARAMS,
} as const;

//#endregion

/**
 * Default export of the ERRORS object for convenient importing.
 */
export default ERRORS;
